import { CircularProgress, styled } from '@mui/material';
import { Delete, Edit, FlashOn } from '@mui/icons-material';
import {
  EIS_ADD,
  KI_EIS_ADD,
  createEisEditRoute,
  createKiFundCertificatesBatchEditRoute,
} from 'adminConstants/routes';
import { dateToLabel } from 'further-ui/utils';
import {
  EIS1Download,
  EIS3Download,
  EIS5Download,
} from 'further-ui/components/SVGIcons';
import { useDownloadByEisWizardId } from 'hooks/data/eisWizard/useDownloadEisCertificatePdfs';
import { FC } from 'react';
import { Disclosure } from 'further-ui/hooks';
import { Api } from 'further-types/eis-wizard';
import useDownloadKiFundCertificates from 'hooks/data/kiFundCertificate/useDownloadKiFundCertificates';
import { TableActions } from 'components/Table';
import { useUploadAllocations } from 'hooks/data/allocation/useUploadAllocations';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type Props = {
  tasklistItem: Api.GetEisWizardTaskListResponse['results'][number];
  deleteDisclosure: Disclosure;
};

const LoadingContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
}));

const RowActions: FC<Props> = ({ tasklistItem, deleteDisclosure }) => {
  const {
    shareAllocationUploadId,
    companyId,
    companyName,
    taxReliefElegible,
    createdWithoutCertificates,
    kiFundCertificatesBatchId,
    eisWizard,
    hasKiCertificates,
    isKiFundCertificate,
  } = tasklistItem;
  const downloadCertificates = useDownloadByEisWizardId();
  const downloadEis5Certficates = useDownloadKiFundCertificates();
  const { downloadEIS1Certificate } = useUploadAllocations();
  const [canCreateSeis, canDeleteSeis, canEditSeis] = useHasPermissions([
    RolePermission.CreateSeis,
    RolePermission.DeleteSeis,
    RolePermission.EditSeis,
  ]);

  const isDownloading =
    downloadCertificates.isLoading || downloadEis5Certficates.isDownloading;

  const handleDownloadPdfs = async () => {
    if (kiFundCertificatesBatchId) {
      await downloadEis5Certficates.download(kiFundCertificatesBatchId);
    } else {
      await downloadCertificates.download({
        //@ts-expect-error
        eisWizardId: eisWizard?.id,
        allocationName: dateToLabel(tasklistItem.allocationDate),
        companyName,
        investmentTypeLabel:
          eisWizard?.investmentType === 'EIS'
            ? 'EIS certificates'
            : 'SEIS certificates',
        splitFolders: true,
      });
    }
  };

  const handleDownloadEIS1 = async () => {
    await downloadEIS1Certificate.mutate({
      id: tasklistItem.shareAllocationUploadId ?? '',
    });
  };

  const downloadTitle = kiFundCertificatesBatchId
    ? 'Download EIS5s'
    : 'Download EIS3s';
  const DownloadIcon = kiFundCertificatesBatchId ? EIS5Download : EIS3Download;
  const editItemRoute = eisWizard?.id
    ? createEisEditRoute(eisWizard.id)
    : createKiFundCertificatesBatchEditRoute(kiFundCertificatesBatchId ?? '');

  const tooltipText =
    'Tax certificates for this allocation cannot be newly processed or deleted while an EIS5 relating to this allocation has been created. Please delete the relevant EIS5 and try again.';

  if (isDownloading) {
    return (
      <LoadingContainer>
        <CircularProgress size={17} />
      </LoadingContainer>
    );
  }

  if (eisWizard?.id || kiFundCertificatesBatchId) {
    return (
      <TableActions
        showAsDropdown
        actions={[
          {
            label: 'Edit',
            icon: Edit,
            link: editItemRoute,
            visible: canEditSeis,
            disabled: hasKiCertificates,
            tooltip: hasKiCertificates ? tooltipText : undefined,
            color: 'primary',
          },
          {
            label: 'Delete',
            icon: Delete,
            onClick: () => deleteDisclosure.stageAction(tasklistItem),
            visible: canDeleteSeis,
            disabled: hasKiCertificates,
            tooltip: hasKiCertificates ? tooltipText : undefined,
            color: 'error',
          },
          {
            label: downloadTitle,
            icon: isDownloading
              ? () => <CircularProgress size={18} />
              : DownloadIcon,
            onClick: handleDownloadPdfs,
            disabled: createdWithoutCertificates || isDownloading,
            visible: !createdWithoutCertificates,
            color: 'primary',
          },
        ]}
      />
    );
  }

  if (taxReliefElegible) {
    const link = isKiFundCertificate
      ? `${KI_EIS_ADD}?selectedFundId=${tasklistItem.fundId}`
      : `${EIS_ADD}?presetCompanyId=${companyId}&presetShareAllocationUploadId=${shareAllocationUploadId}`;

    const createActionNoun = isKiFundCertificate ? 'tranche' : 'allocation';
    const isShareConsiderationExit = !!tasklistItem.exitId;
    return (
      <TableActions
        showAsDropdown
        actions={[
          {
            label: `Create certificates for this ${createActionNoun}`,
            icon: FlashOn,
            visible: canCreateSeis,
            link,
            disabled: hasKiCertificates || isShareConsiderationExit,
            tooltip: hasKiCertificates
              ? tooltipText
              : isShareConsiderationExit
              ? 'Tax certificates are not available for share consideration exits.'
              : undefined,
            color: 'primary',
          },
          {
            label: 'Download EIS1 data sheet',
            icon: EIS1Download,
            onClick: handleDownloadEIS1,
            visible: Boolean(tasklistItem.shareAllocationUploadId),
            color: 'primary',
          },
        ]}
      />
    );
  }

  return null;
};

export default RowActions;
